import { queryOptions } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api-fetch.js';

import {
  DriveFileRestoreError,
  driveErrorMessage,
  driveFileItemFromWire,
  driveFileVersionsFromWire,
  type DriveFileItem,
  type DriveFileItemWire,
  type DriveFileVersionList,
} from './drive-files.js';

const ONEDRIVE_BASE = '/v1/onedrive';

export type OneDrivePermissionLevel = 'read' | 'read_write';
export type OneDriveAutomationAccess = 'none' | OneDrivePermissionLevel;
export type OneDriveAccountStatus =
  'active' | 'reconsent_required' | 'account_unavailable';
export type OneDriveScopeStatus =
  | 'active'
  | 'folder_missing'
  | 'account_unavailable'
  | 'scope_conflict'
  | 'access_revoked';

export interface OneDriveAccount {
  connectionId: string;
  displayName: string | null;
  email: string | null;
  status: OneDriveAccountStatus;
  permissionLevel: OneDrivePermissionLevel;
  backgroundUse: boolean;
}

export interface OneDriveScope {
  scopeId: string;
  connectionId: string;
  workspaceId: string;
  alias: string | null;
  originalName: string;
  parentPath: string | null;
  webUrl: string | null;
  interactiveAccess: OneDrivePermissionLevel;
  automationAccess: OneDriveAutomationAccess;
  /**
   * Whether this caller may change or remove the folder. Reading a workspace's folders is every
   * member's, changing one is the connection owner's — and a list can hold folders from more than
   * one connection, so the answer is per row and comes from the server with the row rather than
   * being re-derived here.
   */
  canManage: boolean;
  /**
   * The whole drive, granted by the server because the pair is open — the owner's own personal
   * workspace reaches all of OneDrive. Nobody changes or removes it by hand, and it is not a choice
   * on the list: the folders beside it are knowledge-base choices.
   */
  openGrant: boolean;
  status: OneDriveScopeStatus;
  isDriveRoot: boolean;
  /** A scope may be one file rather than a folder; an older server that omits it reads as a folder. */
  isFile: boolean;
  /**
   * Graph item id of the folder this scope grants. It addresses the folder itself, which is what a
   * knowledge registration of the whole folder needs, and it is the only Graph authority the contract
   * carries — `drive_id` stays out, so a browser still cannot address a drive it was never granted.
   */
  rootItemId: string;
  /**
   * The folders between this one and the drive root, outermost last, as the server read them when
   * the folder was granted.
   *
   * It is what lets a picker say "you cannot add this yet" instead of offering an Add that the
   * server refuses: the overlap rule is symmetric, so a folder holding a granted one inside it is
   * refused exactly as a folder inside a granted one is. Empty from a server too old to send it,
   * which reads as "nothing known to be inside" — the old behaviour, not a wrong one.
   */
  rootAncestorItemIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface OneDriveItem {
  itemId: string;
  parentItemId: string | null;
  name: string;
  kind: 'file' | 'folder';
  contentType: string | null;
  size: number;
  etag: string | null;
  webUrl: string | null;
  lastModifiedAt: string | null;
}

export interface OneDrivePage<T> {
  items: T[];
  nextCursor: string | null;
  indexingDelayPossible: boolean;
}

export interface OneDrivePageOptions {
  parentItemId?: string | null;
  cursor?: string | null;
  limit?: number;
}

interface OneDriveAccountWire {
  connection_id: string;
  display_name: string | null;
  email: string | null;
  status?: string;
  permission_level?: string;
  background_use?: boolean;
}

interface OneDriveScopeWire {
  scope_id: string;
  connection_id: string;
  workspace_id: string;
  alias: string | null;
  original_name: string;
  parent_path: string | null;
  web_url: string | null;
  interactive_access: string;
  automation_access: string;
  can_manage?: boolean;
  open_grant?: boolean;
  status: string;
  is_drive_root: boolean;
  is_file?: boolean;
  root_item_id: string;
  root_ancestor_item_ids?: string[];
  created_at: string;
  updated_at: string;
}

interface OneDriveItemWire {
  item_id: string;
  parent_item_id?: string | null;
  name: string;
  kind: string;
  content_type: string | null;
  size: number;
  etag: string | null;
  web_url: string | null;
  last_modified_at: string | null;
}

interface OneDriveErrorWire {
  error?: {
    code?: string;
    message?: string;
    remediation?: string;
    retryable?: boolean;
    operation_completed?: boolean | null;
    details?: Record<string, unknown>;
  };
}

export class OneDriveApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly remediation: string,
    public readonly retryable: boolean,
    public readonly operationCompleted: boolean | null,
    public readonly details: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'OneDriveApiError';
  }
}

function permissionLevel(value: string): OneDrivePermissionLevel {
  return value === 'read_write' ? 'read_write' : 'read';
}

function automationAccess(value: string): OneDriveAutomationAccess {
  if (value === 'read' || value === 'read_write') return value;
  return 'none';
}

function accountStatus(value: string): OneDriveAccountStatus {
  if (value === 'active' || value === 'reconsent_required') return value;
  return 'account_unavailable';
}

function scopeStatus(value: string): OneDriveScopeStatus {
  if (
    value === 'active' ||
    value === 'folder_missing' ||
    value === 'account_unavailable' ||
    value === 'scope_conflict'
  ) {
    return value;
  }
  return 'access_revoked';
}

function accountFromWire(
  wire: OneDriveAccountWire,
  missingStatus: OneDriveAccountStatus = 'account_unavailable',
): OneDriveAccount {
  return {
    connectionId: wire.connection_id,
    displayName: wire.display_name,
    email: wire.email,
    status: accountStatus(wire.status ?? missingStatus),
    permissionLevel: permissionLevel(wire.permission_level ?? 'read'),
    backgroundUse: wire.background_use === true,
  };
}

function scopeFromWire(wire: OneDriveScopeWire): OneDriveScope {
  return {
    scopeId: wire.scope_id,
    connectionId: wire.connection_id,
    workspaceId: wire.workspace_id,
    alias: wire.alias,
    originalName: wire.original_name,
    parentPath: wire.parent_path,
    webUrl: wire.web_url,
    interactiveAccess: permissionLevel(wire.interactive_access),
    automationAccess: automationAccess(wire.automation_access),
    canManage: wire.can_manage === true && wire.open_grant !== true,
    openGrant: wire.open_grant === true,
    status: scopeStatus(wire.status),
    isDriveRoot: wire.is_drive_root === true,
    isFile: wire.is_file === true,
    rootItemId: wire.root_item_id,
    rootAncestorItemIds: wire.root_ancestor_item_ids ?? [],
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
  };
}

function itemFromWire(wire: OneDriveItemWire): OneDriveItem {
  if (wire.kind !== 'file' && wire.kind !== 'folder') {
    throw new OneDriveApiError(
      502,
      'ONEDRIVE_GRAPH_UNAVAILABLE',
      'OneDrive returned an unsupported item type.',
      'Reload the folder. If the problem continues, try again later.',
      false,
      null,
      { kind: wire.kind },
    );
  }
  return {
    itemId: wire.item_id,
    parentItemId: wire.parent_item_id ?? null,
    name: wire.name,
    kind: wire.kind,
    contentType: wire.content_type,
    size: wire.size,
    etag: wire.etag,
    webUrl: wire.web_url,
    lastModifiedAt: wire.last_modified_at,
  };
}

/** What an error reads as when the service named no message of its own. */
function requestFailedMessage(status: number): string {
  return `OneDrive request failed (${status})`;
}

async function throwApiError(response: Response): Promise<never> {
  const body = (await response.json().catch(() => ({}))) as OneDriveErrorWire;
  const error = body.error;
  throw new OneDriveApiError(
    response.status,
    error?.code ?? 'ONEDRIVE_GRAPH_UNAVAILABLE',
    error?.message?.trim() || requestFailedMessage(response.status),
    error?.remediation?.trim() || 'Try again later.',
    error?.retryable === true,
    error?.operation_completed ?? null,
    error?.details ?? {},
  );
}

function jsonHeaders(extra?: Record<string, string>): Headers {
  return new Headers({ 'Content-Type': 'application/json', ...extra });
}

async function jsonRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await apiFetch(`${ONEDRIVE_BASE}${path}`, init);
  if (!response.ok) return throwApiError(response);
  return (await response.json()) as T;
}

function pageParams(workspaceId: string, options?: OneDrivePageOptions) {
  const params = new URLSearchParams({ workspace_id: workspaceId });
  if (options?.parentItemId) {
    params.set('parent_item_id', options.parentItemId);
  }
  if (options?.cursor) params.set('cursor', options.cursor);
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  return params;
}

function pageFromWire(body: {
  items?: OneDriveItemWire[];
  next_cursor?: string | null;
  indexing_delay_possible?: boolean;
}): OneDrivePage<OneDriveItem> {
  return {
    items: (body.items ?? []).map(itemFromWire),
    nextCursor: body.next_cursor ?? null,
    indexingDelayPossible: body.indexing_delay_possible === true,
  };
}

/**
 * The OneDrive behind ONE connection. A person can reach several Microsoft accounts, so asking for
 * "their" account had no single answer — it returned whichever row the database happened to hand
 * back first.
 */
export async function getOneDriveAccount(
  workspaceId: string,
  connectionId: string,
): Promise<OneDriveAccount | null> {
  const body = await jsonRequest<{ account: OneDriveAccountWire | null }>(
    `/account?${new URLSearchParams({
      workspace_id: workspaceId,
      connection_id: connectionId,
    }).toString()}`,
  );
  return body.account === null ? null : accountFromWire(body.account);
}

export async function createOneDriveSetupSession(
  workspaceId: string,
  connectionId: string,
): Promise<{
  setupToken: string;
  expiresAt: string;
  account: OneDriveAccount;
}> {
  const body = await jsonRequest<{
    setup_token: string;
    expires_at: string;
    account: OneDriveAccountWire;
  }>('/setup-sessions', {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      workspace_id: workspaceId,
      connection_id: connectionId,
    }),
  });
  return {
    setupToken: body.setup_token,
    expiresAt: body.expires_at,
    // A successfully-created setup session proves this short-lived account
    // response is usable even though the setup wire intentionally omits status.
    account: accountFromWire(body.account, 'active'),
  };
}

export async function disconnectOneDriveAccount(
  workspaceId: string,
  connectionId: string,
): Promise<void> {
  const response = await apiFetch(
    `${ONEDRIVE_BASE}/account?${new URLSearchParams({
      workspace_id: workspaceId,
      connection_id: connectionId,
    }).toString()}`,
    { method: 'DELETE' },
  );
  if (!response.ok) await throwApiError(response);
}

export async function listOneDriveSetupItems(
  workspaceId: string,
  setupToken: string,
  options?: OneDrivePageOptions,
): Promise<OneDrivePage<OneDriveItem>> {
  const body = await jsonRequest<{
    items?: OneDriveItemWire[];
    next_cursor?: string | null;
  }>(`/setup/items?${pageParams(workspaceId, options).toString()}`, {
    headers: { 'X-OneDrive-Setup-Token': setupToken },
  });
  return pageFromWire(body);
}

/** A setup search hit, with the folders between the drive root and it, outermost first. */
export interface OneDriveSetupHit extends OneDriveItem {
  ancestors: { itemId: string; name: string }[];
}

/**
 * Searches the whole OneDrive by name and content under the setup capability, so the person
 * choosing folders can find one without walking to it. Each hit carries its folder path, which is
 * what lets the picker open the folder that holds it.
 */
export async function searchOneDriveSetupItems(
  workspaceId: string,
  setupToken: string,
  query: string,
  options?: { cursor?: string | null },
): Promise<{ items: OneDriveSetupHit[]; nextCursor: string | null }> {
  const params = new URLSearchParams({
    workspace_id: workspaceId,
    query: query.trim(),
  });
  if (options?.cursor) params.set('cursor', options.cursor);
  const body = await jsonRequest<{
    items?: (OneDriveItemWire & {
      ancestors?: { item_id: string; name: string }[];
    })[];
    next_cursor?: string | null;
  }>(`/setup/search?${params.toString()}`, {
    headers: { 'X-OneDrive-Setup-Token': setupToken },
  });
  return {
    items: (body.items ?? []).map((wire) => ({
      ...itemFromWire(wire),
      ancestors: (wire.ancestors ?? []).map((folder) => ({
        itemId: folder.item_id,
        name: folder.name,
      })),
    })),
    nextCursor: body.next_cursor ?? null,
  };
}

export async function getOneDriveSetupDrive(
  workspaceId: string,
  setupToken: string,
): Promise<OneDriveItem> {
  const body = await jsonRequest<{
    root?: OneDriveItemWire;
    item?: OneDriveItemWire;
  }>(
    `/setup/drive?${new URLSearchParams({ workspace_id: workspaceId }).toString()}`,
    { headers: { 'X-OneDrive-Setup-Token': setupToken } },
  );
  const root = body.root ?? body.item;
  if (!root) {
    throw new OneDriveApiError(
      502,
      'ONEDRIVE_GRAPH_UNAVAILABLE',
      'OneDrive did not return its root folder.',
      'Try reconnecting the account.',
      false,
      false,
      {},
    );
  }
  return itemFromWire(root);
}

export async function createOneDriveScope(
  setupToken: string,
  input: {
    workspaceId: string;
    rootItemId: string;
    alias?: string | null;
    interactiveAccess: OneDrivePermissionLevel;
    automationAccess: OneDriveAutomationAccess;
  },
): Promise<OneDriveScope> {
  const body = await jsonRequest<{ scope: OneDriveScopeWire }>('/scopes', {
    method: 'POST',
    headers: jsonHeaders({ 'X-OneDrive-Setup-Token': setupToken }),
    body: JSON.stringify({
      workspace_id: input.workspaceId,
      root_item_id: input.rootItemId,
      ...(input.alias !== undefined ? { alias: input.alias } : {}),
      interactive_access: input.interactiveAccess,
      automation_access: input.automationAccess,
    }),
  });
  return scopeFromWire(body.scope);
}

/** The caller's granted folders in this workspace, with the ceiling the count runs against. */
export interface OneDriveScopeBudget {
  scopes: OneDriveScope[];
  /**
   * How many folders one person may grant this workspace. It comes from identity rather than from a
   * constant here: the interface states a count against it, and a second copy of the number in the
   * SPA is a contract nothing typechecks. A service too old to send it reports zero, and the
   * interface then states the count alone.
   */
  scopesLimit: number;
}

export async function listOneDriveScopes(
  workspaceId: string,
): Promise<OneDriveScopeBudget> {
  const body = await jsonRequest<{
    scopes?: OneDriveScopeWire[];
    scopes_limit?: number;
  }>(
    `/scopes?${new URLSearchParams({ workspace_id: workspaceId }).toString()}`,
  );
  return {
    scopes: (body.scopes ?? []).map(scopeFromWire),
    scopesLimit: body.scopes_limit ?? 0,
  };
}

export async function getOneDriveScope(
  scopeId: string,
  workspaceId: string,
): Promise<OneDriveScope> {
  const params = new URLSearchParams({ workspace_id: workspaceId });
  const body = await jsonRequest<{ scope: OneDriveScopeWire }>(
    `/scopes/${encodeURIComponent(scopeId)}?${params.toString()}`,
  );
  return scopeFromWire(body.scope);
}

export async function updateOneDriveScope(
  scopeId: string,
  patch: {
    workspaceId: string;
    alias?: string | null;
    interactiveAccess?: OneDrivePermissionLevel;
    automationAccess?: OneDriveAutomationAccess;
  },
): Promise<OneDriveScope> {
  const body = await jsonRequest<{ scope: OneDriveScopeWire }>(
    `/scopes/${encodeURIComponent(scopeId)}`,
    {
      method: 'PATCH',
      headers: jsonHeaders(),
      body: JSON.stringify({
        workspace_id: patch.workspaceId,
        ...(patch.alias !== undefined ? { alias: patch.alias } : {}),
        ...(patch.interactiveAccess !== undefined
          ? { interactive_access: patch.interactiveAccess }
          : {}),
        ...(patch.automationAccess !== undefined
          ? { automation_access: patch.automationAccess }
          : {}),
      }),
    },
  );
  return scopeFromWire(body.scope);
}

export async function deleteOneDriveScope(
  scopeId: string,
  workspaceId: string,
): Promise<void> {
  const response = await apiFetch(
    `${ONEDRIVE_BASE}/scopes/${encodeURIComponent(scopeId)}?${new URLSearchParams({ workspace_id: workspaceId }).toString()}`,
    { method: 'DELETE' },
  );
  if (!response.ok) await throwApiError(response);
}

export async function listOneDriveItems(
  scopeId: string,
  workspaceId: string,
  options?: OneDrivePageOptions,
): Promise<OneDrivePage<OneDriveItem>> {
  const body = await jsonRequest<{
    items?: OneDriveItemWire[];
    next_cursor?: string | null;
  }>(
    `/scopes/${encodeURIComponent(scopeId)}/items?${pageParams(workspaceId, options).toString()}`,
  );
  return pageFromWire(body);
}

export async function searchOneDriveItems(
  scopeId: string,
  workspaceId: string,
  query: string,
  options?: Omit<OneDrivePageOptions, 'parentItemId'>,
): Promise<OneDrivePage<OneDriveItem>> {
  const params = new URLSearchParams({
    workspace_id: workspaceId,
    query: query.trim(),
  });
  if (options?.cursor) params.set('cursor', options.cursor);
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  const body = await jsonRequest<{
    items?: OneDriveItemWire[];
    next_cursor?: string | null;
    indexing_delay_possible?: boolean;
  }>(`/scopes/${encodeURIComponent(scopeId)}/search?${params.toString()}`);
  return pageFromWire(body);
}

export async function downloadOneDriveItem(
  scopeId: string,
  itemId: string,
  workspaceId: string,
): Promise<Blob> {
  const params = new URLSearchParams({ workspace_id: workspaceId });
  const response = await apiFetch(
    `${ONEDRIVE_BASE}/scopes/${encodeURIComponent(scopeId)}/items/${encodeURIComponent(itemId)}/content?${params.toString()}`,
  );
  if (!response.ok) return throwApiError(response);
  return response.blob();
}

export async function getOneDriveItem(
  scopeId: string,
  itemId: string,
  workspaceId: string,
): Promise<OneDriveItem> {
  const params = new URLSearchParams({ workspace_id: workspaceId });
  const body = await jsonRequest<{ item: OneDriveItemWire }>(
    `/scopes/${encodeURIComponent(scopeId)}/items/${encodeURIComponent(itemId)}?${params.toString()}`,
  );
  return itemFromWire(body.item);
}

export async function uploadOneDriveFile(
  scopeId: string,
  workspaceId: string,
  parentItemId: string | null,
  file: File,
): Promise<OneDriveItem> {
  const body = new FormData();
  body.set('workspace_id', workspaceId);
  if (parentItemId) body.set('parent_item_id', parentItemId);
  body.set('name', file.name);
  body.set('file', file);
  const result = await jsonRequest<{ item: OneDriveItemWire }>(
    `/scopes/${encodeURIComponent(scopeId)}/files`,
    { method: 'POST', body },
  );
  return itemFromWire(result.item);
}

export async function replaceOneDriveItemContent(
  scopeId: string,
  itemId: string,
  workspaceId: string,
  ifMatch: string,
  file: File,
): Promise<OneDriveItem> {
  const body = new FormData();
  body.set('workspace_id', workspaceId);
  body.set('if_match', ifMatch);
  body.set('file', file);
  const result = await jsonRequest<{ item: OneDriveItemWire }>(
    `/scopes/${encodeURIComponent(scopeId)}/items/${encodeURIComponent(itemId)}/content`,
    { method: 'PUT', body },
  );
  return itemFromWire(result.item);
}

export async function createOneDriveFolder(
  scopeId: string,
  input: { workspaceId: string; parentItemId: string | null; name: string },
): Promise<OneDriveItem> {
  const result = await jsonRequest<{ item: OneDriveItemWire }>(
    `/scopes/${encodeURIComponent(scopeId)}/folders`,
    {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({
        workspace_id: input.workspaceId,
        ...(input.parentItemId ? { parent_item_id: input.parentItemId } : {}),
        name: input.name,
      }),
    },
  );
  return itemFromWire(result.item);
}

export async function renameOneDriveItem(
  scopeId: string,
  itemId: string,
  input: { workspaceId: string; name: string; ifMatch: string },
): Promise<OneDriveItem> {
  const result = await jsonRequest<{ item: OneDriveItemWire }>(
    `/scopes/${encodeURIComponent(scopeId)}/items/${encodeURIComponent(itemId)}`,
    {
      method: 'PATCH',
      headers: jsonHeaders(),
      body: JSON.stringify({
        workspace_id: input.workspaceId,
        name: input.name,
        if_match: input.ifMatch,
      }),
    },
  );
  return itemFromWire(result.item);
}

export async function moveOneDriveItem(
  scopeId: string,
  itemId: string,
  input: {
    workspaceId: string;
    targetScopeId: string;
    targetParentItemId?: string | null;
    name?: string;
    ifMatch: string;
  },
): Promise<OneDriveItem> {
  const result = await jsonRequest<{ item: OneDriveItemWire }>(
    `/scopes/${encodeURIComponent(scopeId)}/items/${encodeURIComponent(itemId)}/move`,
    {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({
        workspace_id: input.workspaceId,
        target_scope_id: input.targetScopeId,
        ...(input.targetParentItemId
          ? { target_parent_item_id: input.targetParentItemId }
          : {}),
        ...(input.name !== undefined ? { name: input.name } : {}),
        if_match: input.ifMatch,
      }),
    },
  );
  return itemFromWire(result.item);
}

export async function trashOneDriveItem(
  scopeId: string,
  itemId: string,
  workspaceId: string,
  ifMatch: string,
): Promise<void> {
  const params = new URLSearchParams({
    workspace_id: workspaceId,
    if_match: ifMatch,
  });
  const response = await apiFetch(
    `${ONEDRIVE_BASE}/scopes/${encodeURIComponent(scopeId)}/items/${encodeURIComponent(itemId)}?${params.toString()}`,
    { method: 'DELETE' },
  );
  if (!response.ok) await throwApiError(response);
}

/**
 * Why the service refused a knowledge registration, in words the user can act on — undefined for a code
 * this build has never seen, which keeps the service's own message.
 *
 * Read by `code`, never by status. The two 403s are a pair worth keeping apart.
 * `ONEDRIVE_KNOWLEDGE_EDITOR_REQUIRED` is the workspace's own role gate, and the sentence for it names the
 * role. `ONEDRIVE_ACCESS_REVOKED` is the OneDrive grant being gone — or a caller the service could not
 * read a role for at all, such as somebody who is not a member — so it keeps the service's words and never
 * answers with a sentence about roles. They used to share one code, and somebody whose folder access had
 * lapsed was sent to ask for a workspace role.
 *
 * `ONEDRIVE_KNOWLEDGE_AUTOMATION_OFF` comes only from a service older than this interface: knowledge no longer
 * depends on the folder's Automation access, so a current service never sends it.
 */
function knowledgeRefusalMessage(error: OneDriveApiError): string | undefined {
  if (error.code === 'ONEDRIVE_KNOWLEDGE_ALREADY_ADDED') {
    return "This item is already in this workspace's knowledge base.";
  }
  if (error.code === 'ONEDRIVE_KNOWLEDGE_EDITOR_REQUIRED') {
    return 'Only an owner or an editor of this workspace can add to the knowledge base.';
  }
  if (error.code === 'ONEDRIVE_KNOWLEDGE_AUTOMATION_OFF') {
    return 'Automation access for this folder is off. Turn it on to add the folder to knowledge.';
  }
  return undefined;
}

/**
 * Registers one item of a connected personal OneDrive folder as a knowledge source of the workspace.
 *
 * The body is a workspace and an item id, and that is deliberate: the drive, the connection and the
 * scope come from the scope the service already verified, so there is no address here for a client to
 * get wrong or to forge. The item id alone buys nothing — the service proves it sits under the scope
 * root before it registers anything.
 */
export async function createOneDriveKnowledgeSource(
  scopeId: string,
  input: { workspaceId: string; itemId: string },
): Promise<{ sourceId: string }> {
  try {
    const body = await jsonRequest<{ source_id: string }>(
      `/scopes/${encodeURIComponent(scopeId)}/knowledge-sources`,
      {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({
          workspace_id: input.workspaceId,
          item_id: input.itemId,
        }),
      },
    );
    return { sourceId: body.source_id };
  } catch (reason) {
    if (reason instanceof OneDriveApiError) {
      const message = knowledgeRefusalMessage(reason);
      if (message !== undefined) {
        throw new OneDriveApiError(
          reason.status,
          reason.code,
          message,
          reason.remediation,
          reason.retryable,
          reason.operationCompleted,
          reason.details,
        );
      }
    }
    throw reason;
  }
}

export const oneDriveKeys = {
  all: ['onedrive'] as const,
  account: (workspaceId: string, connectionId: string) =>
    ['onedrive', 'account', workspaceId, connectionId] as const,
  scopes: (workspaceId: string) => ['onedrive', 'scopes', workspaceId] as const,
  items: (
    workspaceId: string,
    scopeId: string,
    parentItemId: string | null,
    cursor: string | null,
  ) =>
    ['onedrive', 'items', workspaceId, scopeId, parentItemId, cursor] as const,
  search: (
    workspaceId: string,
    scopeId: string,
    query: string,
    cursor: string | null,
  ) => ['onedrive', 'search', workspaceId, scopeId, query, cursor] as const,
  item: (workspaceId: string, scopeId: string, itemId: string) =>
    ['onedrive', 'item', workspaceId, scopeId, itemId] as const,
};

export function oneDriveAccountQueryOptions(
  workspaceId: string,
  connectionId: string,
) {
  return queryOptions({
    queryKey: oneDriveKeys.account(workspaceId, connectionId),
    queryFn: () => getOneDriveAccount(workspaceId, connectionId),
    enabled: connectionId.length > 0,
  });
}

/**
 * The folders themselves. Every surface that lists them wants the list and nothing else, so the
 * budget is selected away here and asked for separately by the one surface that states it.
 */
export function oneDriveScopesQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: oneDriveKeys.scopes(workspaceId),
    queryFn: () => listOneDriveScopes(workspaceId),
    select: (budget: OneDriveScopeBudget) => budget.scopes,
  });
}

/** The same request, kept whole: one fetch answers both through the shared key. */
export function oneDriveScopeBudgetQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: oneDriveKeys.scopes(workspaceId),
    queryFn: () => listOneDriveScopes(workspaceId),
  });
}

export function oneDriveItemsQueryOptions(
  workspaceId: string,
  scopeId: string,
  parentItemId: string | null,
  cursor: string | null,
) {
  return queryOptions({
    queryKey: oneDriveKeys.items(workspaceId, scopeId, parentItemId, cursor),
    queryFn: () =>
      listOneDriveItems(scopeId, workspaceId, { parentItemId, cursor }),
  });
}

/** The path of one OneDrive item inside a scope. */
function scopeItemPath(scopeId: string, itemId: string): string {
  return `/scopes/${encodeURIComponent(scopeId)}/items/${encodeURIComponent(itemId)}`;
}

/** A OneDrive file's stored versions, newest first. */
export async function listOneDriveFileVersions(
  scopeId: string,
  itemId: string,
  workspaceId: string,
): Promise<DriveFileVersionList> {
  const params = new URLSearchParams({ workspace_id: workspaceId });
  const body = await jsonRequest<
    Parameters<typeof driveFileVersionsFromWire>[0]
  >(`${scopeItemPath(scopeId, itemId)}/versions?${params.toString()}`);
  return driveFileVersionsFromWire(body);
}

/** The bytes of one stored version of a OneDrive file. */
export async function downloadOneDriveFileVersion(
  scopeId: string,
  itemId: string,
  versionId: string,
  workspaceId: string,
): Promise<Blob> {
  const params = new URLSearchParams({ workspace_id: workspaceId });
  const response = await apiFetch(
    `${ONEDRIVE_BASE}${scopeItemPath(scopeId, itemId)}/versions/${encodeURIComponent(versionId)}/content?${params.toString()}`,
  );
  if (!response.ok) return throwApiError(response);
  return response.blob();
}

/**
 * Make an older version of a OneDrive file the current one. `ifMatch` is the eTag the caller last
 * read; a file changed since then is refused with 409 instead of overwritten.
 */
export async function restoreOneDriveFileVersion(input: {
  scopeId: string;
  itemId: string;
  versionId: string;
  workspaceId: string;
  ifMatch: string;
}): Promise<DriveFileItem> {
  const response = await apiFetch(
    `${ONEDRIVE_BASE}${scopeItemPath(input.scopeId, input.itemId)}/versions/${encodeURIComponent(input.versionId)}/restore`,
    {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({
        workspace_id: input.workspaceId,
        if_match: input.ifMatch,
      }),
    },
  );
  if (!response.ok) {
    throw new DriveFileRestoreError(
      response.status,
      await driveErrorMessage(response, 'Failed to restore this version'),
    );
  }
  const result = (await response.json()) as { item?: DriveFileItemWire };
  return driveFileItemFromWire(result.item ?? {});
}
